import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import NoRecordsFound from 'components/global/NoRecordsFound';
import SubformHeader from 'components/global/SubformHeader';

import useDebounce from 'hooks/useDebounce';

import { faCubes } from '@fortawesome/pro-solid-svg-icons';

import IntegrationCard from './IntegrationCard';
import * as S from './Integrations.styled';
import { getIntegrationsList } from './integrationsStoresSlice';

/**
 *
 * @author  @muhammedburakak
 */

const Integrations = () => {
	const integrationList = useIntegrationsList();

	const countTabs = useCallback(
		(key) => integrationList?.filter((item) => item.type === key).length,
		[integrationList]
	);

	const tabs = useMemo(
		() => [
			{
				key: 'allIntegrations',
				name: 'allIntegrations',
				size: integrationList.length
			},
			{
				key: 'sms',
				name: 'smsProviders',
				size: countTabs('sms')
			},
			{
				key: 'wms',
				name: 'wmsPartners',
				size: countTabs('wms')
			},
			{
				key: 'dms',
				name: 'dmsPartners',
				size: countTabs('dms')
			},
			{
				key: 'erp',
				name: 'erpPartners',
				size: countTabs('erp')
			},
			{
				key: 'webhooks',
				name: 'webhooks',
				size: countTabs('webhooks')
			},
			{
				key: 'others',
				name: 'others',
				size: countTabs('others')
			}
		],
		[integrationList]
	);

	const [filteredList, setFilteredList] = useState(null);
	const [selectedTab, setSelectedTab] = useState(tabs[0].key);
	const [searchValue, setSearchValue] = useState(null);

	const debouncedValue = useDebounce(searchValue, 500);

	useEffect(() => {
		const data = integrationList?.filter((item) =>
			selectedTab === 'allIntegrations'
				? item
				: item.type.toLowerCase().includes(selectedTab) || item.type.includes(selectedTab)
		);

		setFilteredList(data);
	}, [selectedTab, integrationList]);

	useEffect(() => {
		const selectedTabData =
			selectedTab === 'allIntegrations'
				? integrationList
				: integrationList.filter((item) => item.type === selectedTab);

		if (debouncedValue) {
			setFilteredList(
				selectedTabData?.filter(
					(item) =>
						item.name.toLowerCase().includes(debouncedValue.toLowerCase()) ||
						item.name.includes(debouncedValue.toLowerCase()) ||
						item.name.toLowerCase().split(' ').join('').includes(debouncedValue.toLowerCase())
				)
			);
		} else {
			setFilteredList(selectedTabData);
		}
	}, [debouncedValue]);

	return (
		<S.FlexCard>
			<SubformHeader
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/otherIntegrations-icon.json.gz"
				trigger="hover"
				className="table-lord-icon"
				icon={faCubes}
				boldTitle="otherIntegrations"
				lightTitle="otherIntegrationsSubtitle"
			/>

			<S.InputContainer>
				<SearchInput
					setValue={setSearchValue}
					value={searchValue}
					variant="grey"
					placeholder="searchKeyword"
					fullWidth
				/>
			</S.InputContainer>

			<S.TabContainer>
				{tabs?.map((tab, index) => {
					if (tab?.size <= 0) return null;
					return (
						<Button
							onClick={() => {
								setTimeout(() => {
									setSelectedTab(tab?.key);
									setSearchValue(null);
								}, 100);
							}}
							id={tab?.key}
							variant={selectedTab === tab?.key ? 'black' : 'borderless'}
							key={index}
							label={tab?.name}
							css={S.TabItemCss}>
							{` (${tab?.size})`}
						</Button>
					);
				})}
			</S.TabContainer>

			<S.StoresContainer>
				{integrationList?.length ? (
					filteredList?.length === 0 ? (
						<NoRecordsFound />
					) : (
						filteredList?.map((integrationsStores) => (
							<IntegrationCard
								fieldNames={integrationsStores?.fieldNames}
								badge={integrationsStores?.type}
								key={integrationsStores?.ID}
								integrationID={integrationsStores?.ID}
								title={integrationsStores?.name}
								logo={integrationsStores?.logo}
								content={integrationsStores?.description}
								keyString={integrationsStores?.keyString}
							/>
						))
					)
				) : (
					[...Array(20)].map((_, i) => <IntegrationCard key={i} skeleton={true}></IntegrationCard>)
				)}
			</S.StoresContainer>
		</S.FlexCard>
	);
};

export const useIntegrationsList = () => {
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getIntegrationsList());
	}, []);

	const IntegrationList = useSelector((state) => state?.integrationsStores);

	return IntegrationList;
};

export default Integrations;
